//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import Text from '@components/atoms/text/Text';
import { Checkbox } from '@components/primitives/checkbox/Checkbox';
import { useUniqueId } from '@hooks/useUniqueId';
import { Mixins, TextMixin } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';
import { ITrackableComponent } from '@utils/withTracking';

interface Props
  extends React.ComponentPropsWithoutRef<'button'>,
    ITrackableComponent,
    Pick<React.ComponentProps<typeof Text>, 'ellipsize'> {
  handleClick: (isClicked: boolean) => void;
  isChecked?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  label: string;
  labelMixinOverride?: TextMixin;
  textRef?: React.RefObject<HTMLDivElement>;
  helpText?: string;
}

/**
 * The LabeledCheckbox atom renders a standard Checkbox
 * component with a standardized label next to it
 */
export const LabeledCheckbox = ({
  className,
  ellipsize = 1,
  handleClick,
  helpText,
  isChecked = false,
  isDisabled = false,
  isIndeterminate = false,
  label,
  labelMixinOverride,
  textRef,
  trackingId,
  ...buttonProps
}: Props) => {
  const inputId = useUniqueId('labeled-checkbox-id');

  const onCheckedChange = (checked: boolean) => {
    if (!isDisabled) {
      handleClick(checked);
    }
  };

  return (
    <Container className={className}>
      <Checkbox
        {...buttonProps}
        checked={isIndeterminate ? 'indeterminate' : isChecked}
        disabled={isDisabled}
        id={inputId}
        onCheckedChange={onCheckedChange}
        trackingId={trackingId}
      >
        <Checkbox.Indicator>
          {isChecked && <Checkbox.Icon name='checkmark' />}
          {!isChecked && isIndeterminate && <Checkbox.Icon name='collapse' />}
        </Checkbox.Indicator>
      </Checkbox>
      <LabelContainer htmlFor={inputId} isDisabled={!!isDisabled}>
        <StyledText
          ellipsize={ellipsize}
          height={16}
          isDisabled={!!isDisabled}
          mixin={labelMixinOverride ?? Mixins.Text.Body}
          offset={0}
          textRef={textRef}
        >
          {label}
        </StyledText>
      </LabelContainer>
      {isDefined(helpText) && <HelpIcon helpText={helpText} />}
    </Container>
  );
};

const Container = styled.div(
  ({ theme }) => `
  height: ${theme.spacings[3]};
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  & > * {
    display: inline-block;
  }
`,
);

const LabelContainer = styled.label<{ isDisabled: boolean }>(
  ({ isDisabled, theme }) => css`
    margin: 0 ${theme.spacings[1]} 0 ${theme.spacings[2]};

    user-select: none;
    cursor: pointer;

    ${isDisabled &&
    `
      &:hover {
        cursor: default;
      }
    `}
  `,
);

// If multi-line ellipsize we want to preserve white-space
const StyledText = styled(Text)<{ isDisabled: boolean }>(
  ({ ellipsize, isDisabled, theme }) => css`
    ${isDisabled && `color: ${theme.colors.gray4};`}

    ${isDefined(ellipsize) &&
    (ellipsize === 0 || ellipsize > 1) &&
    `white-space: pre;`}
  `,
);

export default LabeledCheckbox;
